import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * MS557: hooks for mid-period budget adjustments.
 *
 * Backed by `company_budget_adjustments` (landed in MS553) which is an
 * INSERT-only audit log:
 *   - SELECT/INSERT granted to authenticated; RLS scopes to the row's
 *     own company.
 *   - UPDATE/DELETE intentionally not granted AND no RLS policies
 *     defined for those commands — the audit trail is immutable at two
 *     layers (GRANT + RLS). See MS553 migration §6.
 *   - amount_cents is signed: positive = top-up, negative = reduction.
 *   - kind: 'one_time' (this period only) vs 'permanent' (template
 *     change going forward). The 'permanent' case still inserts an
 *     audit row here AND opens the Budget settings flow so the
 *     monthly_budget column updates too. The audit row pins the
 *     decision point for downstream reporting.
 *
 * Why a separate hook file rather than extending useCurrentBudgetPeriod:
 *   - MS556's hook is read-only (the widget never writes). Mixing
 *     mutations in would muddy the test surface — that file's tests
 *     pin pure SELECT behaviour.
 *   - MS557 is the first place an adjustment is *written* from the
 *     buyer-facing UI; isolating the mutation makes the immutability
 *     contract test trivial to assert (no incidental UPDATE/DELETE
 *     paths exist in the codebase to mock around).
 *
 * The generated supabase types do not yet include
 * company_budget_adjustments columns (regenerated post-MS554 merge).
 * We cast at the from() site with `as never` — mirrors the pattern
 * MS556 uses for the same table and MS555 uses for the tier tables.
 */

/**
 * A company_budget_adjustments row. Kept local — see header comment
 * on MS556's CompanyBudgetPeriod for the same reasoning. The audit
 * drawer (Step 4 of this PR) needs created_by joined to the actor's
 * full_name; we surface that on a separate `AdjustmentWithActor`
 * type to keep the bare audit row callers (e.g. the totals computation)
 * free of join clutter.
 */
export interface BudgetAdjustmentRow {
  id: string;
  company_id: string;
  period_id: string;
  amount_cents: number;
  kind: "one_time" | "permanent";
  reason: string | null;
  created_by: string | null;
  created_at: string;
}

/**
 * Audit row joined to the actor's display name. created_by FKs
 * profiles; profiles.full_name + email are public per existing RLS
 * (profiles policies don't redact your own org's display names).
 * When created_by is null (legacy migration backfill or actor row
 * since deleted), `actor_name` is null too — the UI falls back to
 * "Removed user".
 */
export interface AdjustmentWithActor extends BudgetAdjustmentRow {
  actor_name: string | null;
  actor_email: string | null;
}

/**
 * Aggregated view for the modal's read-side and the dashboard
 * widget's capacity calculation. `totalCents` is the signed sum of
 * every adjustment for the period (positive = net top-up).
 */
export interface AdjustmentsForPeriod {
  adjustments: AdjustmentWithActor[];
  totalCents: number;
}

/**
 * Lists every adjustment for a period, joined to the actor's profile,
 * most-recent first. Used by:
 *   - The audit history surface on Settings → Budget (Step 4).
 *   - The modal's read-after-write confirmation (so the buyer sees
 *     their just-inserted row land in the list).
 *
 * The query is partitioned by periodId so it doesn't span periods —
 * the audit list always answers "what happened to *this* period."
 * For cross-period auditing (future ticket), a separate hook will
 * read by companyId without a period filter.
 *
 * `limit` defaults to 50 — well above the "last 10 by default" UI
 * cap but generous enough that "Show more" can expand client-side
 * without a refetch. Pass a higher number if you need the full log.
 */
export function useAdjustmentsForPeriod(
  periodId: string | null | undefined,
  limit = 50,
) {
  return useQuery({
    queryKey: ["budget-adjustments", periodId, limit],
    enabled: Boolean(periodId),
    queryFn: async (): Promise<AdjustmentsForPeriod> => {
      if (!periodId) return { adjustments: [], totalCents: 0 };
      // PostgREST embed: `created_by_profile:profiles!created_by(...)`
      // — the relation hint disambiguates because profiles has multiple
      // potential references (here only created_by, but we name it
      // explicitly for forward-compat). Falls back gracefully when the
      // join row is null (created_by NULL after a profile deletion
      // cascades the FK to NULL via ON DELETE SET NULL — see MS553
      // schema §3).
      const { data, error } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from("company_budget_adjustments" as any)
        .select(
          "id, company_id, period_id, amount_cents, kind, reason, created_by, created_at, created_by_profile:profiles!created_by(full_name, email)",
        )
        .eq("period_id", periodId)
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      const rows =
        (data as unknown as Array<
          BudgetAdjustmentRow & {
            created_by_profile:
              | { full_name: string | null; email: string | null }
              | null;
          }
        > | null) ?? [];
      const adjustments: AdjustmentWithActor[] = rows.map((r) => ({
        id: r.id,
        company_id: r.company_id,
        period_id: r.period_id,
        amount_cents: r.amount_cents,
        kind: r.kind,
        reason: r.reason,
        created_by: r.created_by,
        created_at: r.created_at,
        actor_name: r.created_by_profile?.full_name ?? null,
        actor_email: r.created_by_profile?.email ?? null,
      }));
      const totalCents = adjustments.reduce(
        (acc, a) => acc + (a.amount_cents ?? 0),
        0,
      );
      return { adjustments, totalCents };
    },
  });
}

/**
 * Input to the create-adjustment mutation. amountCents is signed
 * (positive = top-up, negative = reduction). companyId + periodId
 * + kind are required by the schema. createdBy lets the caller pass
 * the current profile id; null is acceptable (the FK cascades to null
 * on profile deletion anyway).
 *
 * `reason` is optional free-text. The modal trims and stores empty
 * strings as null so the column matches "no reason given" deterministically.
 */
export interface CreateAdjustmentInput {
  companyId: string;
  periodId: string;
  amountCents: number;
  kind: "one_time" | "permanent";
  reason: string | null;
  createdBy: string | null;
}

/**
 * Inserts an audit row. By design this is INSERT-only; the hook
 * exposes no update/delete path. The mutation invalidates:
 *   - ["budget-adjustments", periodId, ...] so the audit list refetches.
 *   - ["budget-period-totals", ...] so the dashboard widget's
 *     capacity recomputes immediately (MS556's hook reads the same
 *     adjustments table).
 *
 * The invalidation uses a prefix match (queryKey: [...] with no
 * `exact: false`) so every limit variant of the audit list refetches.
 * Same pattern MS555's tier mutation uses for ["celebration_tiers",
 * companyId, "with-counts"].
 *
 * Never blocks. Per MS552 §6 + the dispatch lock — even if the new
 * row would drive capacity negative, the INSERT proceeds. The widget
 * shows the negative via Stat tone="warn" but no flow is blocked.
 * Tests pin this directly via the "modal allows negative-driving
 * adjustments" case.
 */
export function useCreateAdjustment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: CreateAdjustmentInput,
    ): Promise<BudgetAdjustmentRow> => {
      const { data, error } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from("company_budget_adjustments" as any)
        .insert({
          company_id: input.companyId,
          period_id: input.periodId,
          amount_cents: input.amountCents,
          kind: input.kind,
          reason: input.reason,
          created_by: input.createdBy,
        })
        .select(
          "id, company_id, period_id, amount_cents, kind, reason, created_by, created_at",
        )
        .single();
      if (error) throw error;
      return data as unknown as BudgetAdjustmentRow;
    },
    onSuccess: (_row, input) => {
      // The audit list query keys are prefixed by ["budget-adjustments",
      // periodId, limit]. Invalidate-by-prefix catches every cached
      // limit variant in one call.
      void queryClient.invalidateQueries({
        queryKey: ["budget-adjustments", input.periodId],
      });
      // MS556's widget reads adjustments via the totals hook keyed by
      // ["budget-period-totals", companyId, periodId, ...]. Invalidate
      // by the leading prefix so the widget refetches its capacity.
      // MS558's burn-rate hook composes the same totals — no
      // separate invalidation needed; the forecast re-evaluates when
      // its underlying totals query refetches.
      void queryClient.invalidateQueries({
        queryKey: ["budget-period-totals"],
      });
    },
  });
}
